import type { AudiencePerson, Notice } from '@/shared/notices/types';
import { PROFILE_LABELS, type UserProfile } from '@/shared/session/profiles';
import type { Tone } from '@/shared/ui/Badge';
import { formatDate } from '@/shared/utils/format';

const whenFormat = new Intl.DateTimeFormat('es-ES', { dateStyle: 'short', timeStyle: 'short' });
const COUNT = new Intl.NumberFormat('es-ES');

/** «Víctor · 15/10/26, 10:32», or the date alone when nobody signs it. */
export function noticeByline(notice: Notice): string {
  const when = formatDate(whenFormat, notice.created_at);
  return notice.created_by ? `${notice.created_by} · ${when}` : when;
}

/** What the link of a notice says: a tariff's opens its supplier, a payroll's its month. */
export function noticeLinkLabel(notice: Notice): string {
  if (notice.kind === 'tariff') return 'Ver el proveedor y sus tarifas';
  if (notice.kind === 'payroll') return 'Abrir las nóminas';
  return 'Abrir';
}

const PAYROLL_TONES: Record<NonNullable<Notice['tone']>, { tone: Tone; label: string }> = {
  info: { tone: 'info', label: 'Nóminas' },
  warn: { tone: 'warn', label: 'Nóminas · por revisar' },
  danger: { tone: 'danger', label: 'Nóminas · con fallos' },
};

/**
 * The colour of a payroll notice, as the month shows it (contract v10.54, ADR 0050 of the
 * back): blue when there is nothing to fix, amber when something has to be looked at, red
 * when something failed or did not leave. `null` for every other notice, which has none.
 * The label says it in words too: the colour alone is not enough.
 */
export function noticeToneBadge(notice: Notice): { tone: Tone; label: string } | null {
  if (notice.kind !== 'payroll') return null;
  return PAYROLL_TONES[notice.tone ?? 'info'];
}

/**
 * How the email to «Comerciales» went, for whoever administers (contract v10.50). `null`
 * when the notice did not go by email, or the person does not administer. `detail` is
 * what the sending said when it failed, in its own sentence.
 */
export function noticeEmailLine(
  notice: Notice,
): { text: string; failed: boolean; detail: string | null } | null {
  const email = notice.email;
  if (!email) return null;
  const who = `${COUNT.format(email.recipients)} ${email.recipients === 1 ? 'comercial' : 'comerciales'}`;
  if (email.status === 'sent') {
    // Sent with a note: some address the server refused, as the mail manager says too.
    return { text: `Enviado por correo a ${who}.`, failed: false, detail: email.error ?? null };
  }
  if (email.status === 'failed') {
    return { text: `No salió el correo a ${who}.`, failed: true, detail: email.error ?? null };
  }
  return { text: `Saliendo por correo a ${who}…`, failed: false, detail: null };
}

/**
 * Whom a notice goes to, for whoever administers (contract v10.55, ADR 0052 of the back):
 * «Para todo el equipo», «Para: Tienda, Juan Pérez», and for a payroll notice whoever sends
 * it, whom its kind decides. Commas and no «y»: «Dirección y compras» already has one. Says
 * so when it does not come up to this person, who sees it only to know and to withdraw it.
 * `null` for anybody else, who gets only theirs, and with a back before v10.55.
 */
export function noticeAudienceLine(notice: Notice): string | null {
  const audience = notice.audience;
  if (!audience) return null;
  let to: string;
  if (notice.kind === 'payroll') {
    to = 'Para quien envía las nóminas';
  } else if (audience.everyone) {
    to = 'Para todo el equipo';
  } else {
    const names = [
      ...(audience.profiles ?? []).map((profile) => PROFILE_LABELS[profile]),
      ...(audience.people ?? []).map((person) => person.name),
    ];
    to =
      names.length > 0
        ? `Para: ${names.join(', ')}`
        : 'Para nadie: las personas a las que iba ya no están';
  }
  return notice.for_me === false ? `${to} · a ti no te sale` : to;
}

/**
 * How many people a notice would reach as chosen in the window: anybody whose profile is
 * chosen or who is chosen by name. A superadministrator has no profile here (`null`): only
 * by name. Whoever writes is not in the list and is not counted: they have it seen.
 */
export function audienceCount(
  people: readonly AudiencePerson[],
  profiles: ReadonlySet<UserProfile>,
  chosen: ReadonlySet<string>,
): number {
  return people.filter(
    (person) => chosen.has(person.id) || (person.profile != null && profiles.has(person.profile)),
  ).length;
}

/** Without accents nor capitals: «jose» finds «José». */
function fold(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

/** Whether a person of the window answers to what was typed, by name or address. Nothing
 * typed: everybody. */
export function matchesPerson(person: AudiencePerson, typed: string): boolean {
  const wanted = fold(typed.trim());
  return !wanted || fold(`${person.name} ${person.email}`).includes(wanted);
}

/** «1 persona», «3 personas», «nadie». */
export function peopleCount(count: number): string {
  if (count === 0) return 'nadie';
  return `${COUNT.format(count)} ${count === 1 ? 'persona' : 'personas'}`;
}

/** A path of the platform, and only that: the back writes `/documentos/...`, and a link
 * that is not one (`//elsewhere`, `https:`, `/\elsewhere`, which a browser reads as
 * `//elsewhere`) is not followed. */
export function internalLink(link: string | null | undefined): string | null {
  if (!link || !link.startsWith('/') || link.startsWith('//')) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\\\u0000-\u001f\u007f]/.test(link)) return null;
  const base = 'https://cubas.invalid';
  try {
    if (new URL(link, base).origin !== base) return null;
  } catch {
    return null;
  }
  return link;
}

/** Today plus `days`, in Madrid, as a date input wants it. Days of the calendar, not
 * 24-hour blocks: across the October change of hour they are not the same. */
export function dayInput(days: number, now = new Date()): string {
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Madrid',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
  const [year, month, day] = today.split('-').map(Number);
  const later = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, (day ?? 1) + days));
  return later.toISOString().slice(0, 10);
}
